import availableLanguages from '../../resources/languages.json';
import type { WlWindow } from '../../typings/globals/cordova';

declare const window: WlWindow;

export function cleanLang(preferredLanguage: string): string {
  if (!preferredLanguage) return 'en';
  const preferredLanguageStripped = preferredLanguage.substring(0, 2);
  return availableLanguages.indexOf(preferredLanguageStripped) === -1
    ? 'en'
    : preferredLanguageStripped;
}

export const getLangFromUrl = (): string | null => {
  try {
    const pathname = window.location?.pathname ?? '';
    const [lang] = pathname
      .split('/')
      .filter((el) => availableLanguages.indexOf(el) > -1);
    if (!lang && (window as any).isPreRender) {
      return 'en';
    }
    return lang || null;
  } catch {
    return null;
  }
};

export const getLanguageFromLocalStorage = (): string | null => {
  try {
    const value = window.localStorage?.getItem('langCode');
    if (value && value !== 'undefined') return value.toLowerCase();
    return null;
  } catch {
    return null;
  }
};

export const saveLanguageToLocalStorage = (lang: string): void => {
  try {
    window.localStorage?.setItem('langCode', lang);
  } catch {}
};

export const getLangFromBrowser = (): string | null => {
  try {
    return navigator.language || (navigator as any).userLanguage || null;
  } catch {
    return null;
  }
};

export const getPreferredLanguage = (): string => {
  const langFromUrl = getLangFromUrl();
  const langFromStorage = getLanguageFromLocalStorage();
  const langFromBrowser = getLangFromBrowser();
  return cleanLang(langFromUrl || langFromStorage || langFromBrowser || 'en');
};

export type LanguageSource = 'url' | 'storage' | 'browser' | 'default';

export const getLanguageSource = (currentLang: string): LanguageSource => {
  const langFromUrl = getLangFromUrl();
  const langFromStorage = getLanguageFromLocalStorage();
  const langFromBrowser = getLangFromBrowser();
  if (currentLang && currentLang === langFromUrl) return 'url';
  if (currentLang && currentLang === langFromStorage) return 'storage';
  if (currentLang && currentLang === langFromBrowser) return 'browser';
  return 'default';
};

export async function loadDateLocale(lang: string) {
  switch (lang) {
    case 'fr':
      return import('date-fns/locale/fr');
    case 'de':
      return import('date-fns/locale/de');
    case 'nl':
      return import('date-fns/locale/nl');
    case 'es':
      return import('date-fns/locale/es');
    case 'it':
      return import('date-fns/locale/it');
    default:
      return import('date-fns/locale/en-IN');
  }
}

export async function loadTranslations(lang: string) {
  const mod: any = await import(`resources/${lang}.i18n.json`);
  if (typeof mod === 'function') return mod();
  return mod?.default ?? mod;
}

export function getLanguageList(): { code: string; name: string }[] {
  const names: Record<string, string> = {
    en: 'English',
    es: 'Español',
    nl: 'Nederlands',
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
  };
  return availableLanguages
    .filter((code) => code in names)
    .map((code) => ({ code, name: names[code] }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
